import type { BulleEnrichie, Etat, Priorite } from '../partage/api/types';
import { PastilleJamaisOuverte, PointsDeContenu, PastilleEtat } from '../apparence';
import { useDeuxGestes } from '../partage/deuxGestes';
import { attributsDeLHabit, SilhouetteDObjet, type HabitDessine } from '../partage/objetsDeBulle';
import { MenuDeLHabit, type HabitDuProjetPourUneBulle } from '../fiche';
import { effacementDuNoeud } from './effacement';
import { TitreSousLaLoupe } from '../partage/TitreSousLaLoupe';
import { classeDeLaLueur, DureeEnAnalyse, ReboursEnAnalyse } from '../partage/BulleEnAnalyse';
import type { LoupeSurUneBulle } from '../partage/loupeDeLaCarte';
import {
  DEUX_GESTES,
  etatSuivant as suivant,
  etiquetteDuCalque,
  IDEE,
  ligneDeProprietes,
  titreDuCalque,
} from '../partage/vocabulaire';
import './NoeudCarte.css';

/**
 * **Un nœud n'est pas un pavé, c'est une étape classée.** Sur la carte, il porte aussi la
 * couleur de son axe, et sa petite flèche l'ouvre sur place (ticket 06 de retours-testeurs).
 *
 * Il porte son canal et sa durée dans une ligne discrète sous son titre — le
 * même vocabulaire que la ligne de propriétés de la fiche. Une seule grammaire pour les
 * mêmes valeurs, jamais deux : c'est pour ça qu'il lit `ligneDeProprietes` au lieu d'écrire
 * ses propres mots.
 *
 * Son rang, lui, n'est pas une propriété parmi d'autres : la planche remplace le glyphe de
 * tête par le chiffre. Un rang est un jugement, il se lit d'un coup d'œil ou il ne sert à
 * rien.
 */
export function NoeudCarte({
  bulle,
  x,
  y,
  axe,
  habit,
  habitDuProjet,
  dedans,
  ouverte,
  depliee,
  depliable,
  rangEnAttente,
  premiere = false,
  enHierarchisation,
  cachee = false,
  glissee = false,
  inerte = false,
  taille = 1,
  combienDansLeCalque = null,
  loupe,
  onDescendre,
  onFiche,
  onEtat,
  onDeplier,
  onRemettreASaPlace = null,
}: {
  bulle: BulleEnrichie;
  x: number;
  y: number;
  /**
   * Sa couleur d'axe, `var(--axe-N)` : celle de l'étape du premier tour qui la contient
   * (ticket 06 de retours-testeurs). La bulle elle-même est colorée, et son trait aussi.
   */
  axe: string | null;
  /**
   * Son habit (chantier habit-des-bulles, modification 03) : sa forme ou son objet, son trait,
   * les siens sinon ceux du projet ; rien au téléphone.
   */
  habit: HabitDessine;
  /** L'habit du projet, pour son menu « ⋯ » (habit-des-bulles, modification 05) ; `null` : pas de menu. */
  habitDuProjet: HabitDuProjetPourUneBulle | null;
  /** Dans une étape ouverte sur place, pas au premier tour : sa couleur est plus claire. */
  dedans: boolean;
  /** La branche d'où l'on vient de remonter. */
  ouverte: boolean;
  /** Ouverte sur place : ses étapes l'entourent. */
  depliee: boolean;
  /** Ses étapes sont connues de la carte : sa petite flèche l'ouvre sur place. */
  depliable: boolean;
  rangEnAttente: number | null;
  /** La première bulle de « Lier » (chantier liens-entre-bulles, ticket 04) : marquée comme une numérotée. */
  premiere?: boolean;
  /** Hiérarchiser ou « Lier » en cours : un clic la désigne, ses autres gestes sont hors de portée. */
  enHierarchisation: boolean;
  /**
   * Pas à l'écran : l'étape qui la contient n'est pas ouverte. Elle attend, invisible, sur
   * sa mère, pour en sortir quand on l'ouvre.
   */
  cachee?: boolean;
  /** Celle qu'on glisse, cadenas ouvert, ou qu'on vient de lâcher (chantier deplacer-les-bulles, ticket 03). */
  glissee?: boolean;
  /** Visible mais hors du geste en cours : une bulle dépliée pendant qu'on hiérarchise. */
  inerte?: boolean;
  /**
   * Sa taille selon le filtre (chantier `revue-design`, ticket 04, `filtreDeLaCarte.ts`) : 1
   * sans filtre ou au niveau 1 ; plus grande si elle porte l'importance mise en avant, plus
   * petite sinon. Le serveur l'a placée à cette taille ; elle reste cliquable.
   */
  taille?: number;
  /** Une étape allumée par le filtre : combien elle porte de cette importance. */
  combienDansLeCalque?: { priorite: Priorite; combien: number } | null;
  /**
   * Les touches de la carte (chantier modes-de-la-carte, modification 02, `loupeSurLaBulle`) : ce
   * qu'elle dit à la place de son titre ; son titre pâli quand elle n'a pas l'info, ou quand la
   * recherche ne l'a pas trouvée. Sa place ne bouge pas : l'info se coupe comme un titre. En
   * Analyse (theme-analyse, ticket 02) : sa durée à la place de la ligne des réglages, son J-n.
   */
  loupe?: LoupeSurUneBulle;
  onDescendre: (id: string) => void;
  onFiche: (id: string) => void;
  onEtat: (id: string, etat: Etat) => void;
  /** La petite flèche : ouvrir ou refermer sur place. */
  onDeplier: (id: string) => void;
  /**
   * Déplacée à la profondeur affichée (chantier deplacer-les-bulles, ticket 04) : son « ⋯ » propose
   * « Remettre à sa place » ; `null` : la ligne ne s'y montre pas.
   */
  onRemettreASaPlace?: (() => void) | null;
}) {
  const gestes = useDeuxGestes(
    () => onDescendre(bulle.id),
    () => onFiche(bulle.id)
  );

  const etat = bulle.etat;
  const analyse = loupe?.analyse ?? null;
  // En Analyse, la durée seule remplace la ligne des réglages (maquette `contenu`).
  const detail = analyse ? (analyse.duree ? [analyse.duree] : []) : ligneDeProprietes(bulle);

  /*
   * En pointillé, et rien d'autre : jamais ouverte (« Rien dedans » à l'écran). Une bulle
   * sans enfants est une intention, une bulle avec des enfants est un engagement (SPEC §4).
   *
   * Mais seulement tant qu'elle est « à faire ». Une bulle sur laquelle tu as agi — en
   * instruction, acquise, écartée — n'est plus une intention, même si tu ne l'as jamais
   * creusée : la dessiner en pointillé dirait le contraire de ce qui s'est passé.
   */
  const commeUneIntention = bulle.jamaisOuverte && etat === 'a_faire';
  const posee = etat === 'fait';
  /*
   * En réflexion (ticket 18 de retours-testeurs) : une idée, pas une chose faite. Elle ne
   * s'efface donc pas comme une bulle acquise : un bord en pointillé et l'étiquette « Idée »
   * à la place de la ligne des réglages. Le pointillé seul dirait « Rien dedans » (ticket 03) :
   * c'est l'étiquette qui distingue.
   */
  const enReflexion = etat === 'sans_objet';

  /*
   * Le rang posé remplace le glyphe de tête, et seulement tant que la bulle est devant toi :
   * sur une bulle acquise, c'est la coche qui a le dernier mot, et une idée n'est pas encore
   * dans ton ordre. Il pâlit tant qu'une condition la retient : un ordre qu'on ne peut pas
   * encore suivre n'est pas encore le tien.
   */
  const rangDeTete = bulle.rang !== null && !posee && !enReflexion ? bulle.rang : null;

  const classes = [
    'noeud',
    axe ? 'noeud--axe' : '',
    dedans ? 'noeud--dedans' : '',
    commeUneIntention ? 'noeud--jamais-ouverte' : '',
    ouverte ? 'noeud--ouvert' : '',
    depliee ? 'noeud--depliee' : '',
    etat === 'en_attente_confirmation' ? 'noeud--instruction' : '',
    posee ? 'noeud--eteint' : '',
    enReflexion ? 'noeud--reflexion' : '',
    detail.length === 0 && !enReflexion ? 'noeud--nu' : '',
    rangEnAttente !== null ? 'noeud--numerote' : '',
    premiere ? 'noeud--premiere' : '',
    glissee ? 'noeud--glissee' : '',
    // Une seule raison de pâlir : une bulle cachée le reste, hiérarchisation ou non (`effacement.ts`).
    effacementDuNoeud({ cachee, inerte }) ?? '',
    taille > 1 ? 'noeud--grossie' : taille < 1 ? 'noeud--reduite' : '',
    loupe?.info ? 'noeud--info' : '',
    loupe?.sansInfo ? 'noeud--sans-info' : '',
    loupe?.terne ? 'noeud--terne' : '',
    loupe?.allumee ? 'noeud--allumee' : '',
    classeDeLaLueur(analyse),
  ]
    .filter(Boolean)
    .join(' ');
  const horsDePortee = enHierarchisation || cachee || inerte;
  const dessin = attributsDeLHabit(habit);

  return (
    <div
      className={dessin.classe ? `${classes} ${dessin.classe}` : classes}
      {...dessin.attributs}
      style={{
        ...dessin.style,
        left: x,
        top: y,
        ...(axe ? { ['--axe' as string]: axe } : {}),
        ...(taille !== 1 ? { ['--taille' as string]: taille } : {}),
      }}
      aria-hidden={cachee || undefined}
      data-tuto="bulle"
      data-bulle={bulle.id}
    >
      {habit.objet && <SilhouetteDObjet objet={habit.objet} />}
      {rangEnAttente !== null && <span className="noeud-rang-attente">{rangEnAttente}</span>}
      <ReboursEnAnalyse analyse={analyse} />
      {habitDuProjet && (
        <MenuDeLHabit
          bulle={bulle}
          projet={habitDuProjet}
          horsDePortee={horsDePortee}
          onRemettreASaPlace={onRemettreASaPlace}
        />
      )}

      <button
        type="button"
        className="noeud-pastille"
        onClick={() => onEtat(bulle.id, suivant(etat))}
        aria-label="changer l’état"
        tabIndex={horsDePortee ? -1 : 0}
      >
        {rangDeTete !== null ? (
          <span className={`noeud-rang${bulle.bloquee ? ' noeud-rang--loin' : ''}`}>
            {rangDeTete}
          </span>
        ) : commeUneIntention ? (
          <PastilleJamaisOuverte taille={15} />
        ) : (
          <PastilleEtat etat={etat} taille={15} />
        )}
      </button>

      <button
        type="button"
        className="noeud-corps"
        {...gestes}
        tabIndex={horsDePortee ? -1 : 0}
        title={DEUX_GESTES}
      >
        <TitreSousLaLoupe classe="noeud-titre" titre={bulle.titre} info={loupe?.info ?? null} />
        {enReflexion ? (
          <span className="etiquette-idee">{IDEE}</span>
        ) : analyse ? (
          <DureeEnAnalyse classe="noeud-detail" analyse={analyse} />
        ) : (
          detail.length > 0 && <span className="noeud-detail">{detail.join(' · ')}</span>
        )}
        {combienDansLeCalque && (
          <span
            className="etiquette-calque"
            title={titreDuCalque(combienDansLeCalque.priorite, combienDansLeCalque.combien)}
          >
            {etiquetteDuCalque(combienDansLeCalque.priorite, combienDansLeCalque.combien)}
          </span>
        )}
        {/* Ce qu'elle contient se COMPTE d'un regard : un point par chose, jamais un nombre. */}
        <PointsDeContenu combien={bulle.nbEnfants} />
      </button>

      {/* La petite flèche ouvre l'étape sur place, ou la referme, au clic (ticket 06 de
          retours-testeurs) : on voit ce qu'elle contient sans entrer dedans. Tournée d'un
          quart de tour quand elle est ouverte. Si ses étapes ne sont pas connues de la
          carte (un arbre énorme), elle fait entrer, comme le corps. */}
      {bulle.nbEnfants > 0 &&
        (depliable ? (
          <button
            type="button"
            className="noeud-chevron noeud-deplier"
            onClick={() => onDeplier(bulle.id)}
            aria-label={depliee ? 'replier' : 'déplier sur place'}
            aria-expanded={depliee}
            title={depliee ? 'Replier' : 'Déplier sur place'}
            tabIndex={horsDePortee ? -1 : 0}
          >
            <Fleche />
          </button>
        ) : (
          <span className="noeud-chevron" aria-hidden="true" {...gestes}>
            <Fleche />
          </span>
        ))}
    </div>
  );
}

function Fleche() {
  return (
    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">
      <path d="M3 1.4L6.2 4.5L3 7.6" stroke="currentColor" strokeWidth={1.4} />
    </svg>
  );
}
